import { InnovationDetailBenefice } from '../InnovationDetailBenefice/InnovationDetailBenefice.component';
import { InnovationDetailConcept } from '../InnovationDetailConcept/InnovationDetailConcept.component';
import { InnovationDetailContact } from '../InnovationDetailContact/InnovationDetailContact.component';
import { InnovationDetailSimilaire } from '../InnovationDetailSimilaire/InnovationDetailSimilaire.component';
import { InnovationTagList } from '../InnovationTagList/InnovationTagList';
import Error from 'pages/_error';
import { InnovationContext } from 'pages/les-innovations/[slug]';
import { Tag } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useContext, useMemo } from 'react';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { ClippedImage } from '@components/ClippedImage/ClippedImage.component';
import { PlcImg } from '@components/PlcImg/PlcImg.component';
import { Text } from '@components/Text/Text.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { PlcPageDetailInnovationHeader } from '@layout/components/PlcPageDetailInnovationHeader/PlcPageDetailInnovationHeader.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { ApiStatusCode } from '@utils/api.utils';
import { ClippedImageStyle } from '@utils/images';
import { isExternal, urlEnum } from '@utils/url';
import classNames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

import './InnovationDetailLayout.scss';

type Props = ComponentPropsWithRef<'main'> & {};

export const InnovationDetailLayout: FC<Props> = ({ className, ...rest }) => {
  const {
    startup: { data: startup },
    domains: { data: domainsList },
    stakes: { data: stakesList },
    sectors: { data: sectorsList },
  } = useContext(InnovationContext);
  const rootClassNameSector = classNames('content-detail-tags-list', 'plc-tag-sector');
  const rootClassNameStakes = classNames('content-detail-tags-list', 'plc-tag-stake');
  const rootClassNameDomain = classNames('content-detail-tags-list', 'plc-tag-domain');

  const sectors = useMemo(() => {
    if (sectorsList && startup && startup.sectorCodes.length > 0) {
      const sectorsFiltered = sectorsList
        ? sectorsList.filter(sector => startup.sectorCodes.some(sectorCode => sectorCode === sector.remoteId))
        : ([] as Tag[]);
      return <InnovationTagList className={rootClassNameSector} oneLine={true} showAll={true} tags={sectorsFiltered} />;
    }
    return null;
  }, [sectorsList, startup]);
  const stakes = useMemo(() => {
    if (stakesList && startup && startup.stakeCodes.length > 0) {
      const stakesFiltered = stakesList
        ? stakesList.filter(stake => startup.stakeCodes.some(stakeCode => stakeCode === stake.remoteId))
        : ([] as Tag[]);
      return <InnovationTagList className={rootClassNameStakes} oneLine={true} showAll={true} tags={stakesFiltered} />;
    }
    return null;
  }, [stakesList, startup]);
  const domains = useMemo(() => {
    if (domainsList && startup && startup.domainCodes.length > 0) {
      const domainsFiltered = domainsList
        ? domainsList.filter(domain =>
            startup.domainCodes.some(
              domainCode =>
                domainCode === domain.remoteId ||
                (domain.children ? domain.children.map(e => e.remoteId).includes(domainCode) : false)
            )
          )
        : ([] as Tag[]);
      return <InnovationTagList className={rootClassNameDomain} oneLine={true} showAll={true} tags={domainsFiltered} />;
    }
    return null;
  }, [domainsList, startup]);

  const tagProps: PlcTagProps = useMemo(
    () => ({
      level2: ATLevel2.Innovation,
      tagPageNameI18nArray: [startup ? { code: startup.slug, notI18n: true } : undefined],
    }),
    [startup]
  );

  if (!startup) {
    return <Error errorCode={ApiStatusCode.notFound} />;
  }
  const rootClass = classNames(className, 'plc-inno');

  const BREADCRUMB_STEPS: BreadCrumbStep[] = [
    {
      label: 'common.newspaper-short',
      linkProps: { as: urlEnum.journal, href: urlEnum.journal },
    },
    {
      label: 'common.innovations.catalogue-menu-title',
      linkProps: { as: urlEnum.innovations, href: urlEnum.innovations },
    },
    {
      label: startup.name,
      notI18n: true,
    },
  ];

  const startupUrl = isExternal(startup.url) ? startup.url : 'https://'.concat(startup.url);

  return (
    <PlcPageLayout
      className={rootClass}
      metaDescI18n="innovations.home.meta-desc"
      metaTitleI18n="innovations.home.meta-title"
      tagProps={tagProps}
      {...rest}
    >
      <PlcPageDetailInnovationHeader breadcrumbSteps={BREADCRUMB_STEPS} className="plc-mb-xl wrapper-s">
        <div className="grid-detail__header">
          <div className="grid-detail__header_card">
            <PlcImg alt="" height="200px" plcSrc={{ src: startup.logo.url, type: 'external' }} />
            <div className="flex column flex-1 plc-mb-xs min-height plc-mt-l plc-mt-up-l-0">
              {sectors}
              {stakes}
              {domains}
              <Text className="h2-up-s card-title plc-mt-s" tag="p" variant="h3">
                {startup.name}
              </Text>
              <Text className="card-description" variant="p">
                {startup.description}
              </Text>
              <div className="card-header-bottom">
                <PlcAnchor linkProps={{ as: startupUrl, href: startupUrl }} target="_blank">
                  <PrimaryButton>{startup.titleButtonUrl}</PrimaryButton>
                </PlcAnchor>
              </div>
            </div>
          </div>
          <div className="grid-detail__header_img plc-pt-basis">
            <ClippedImage
              alt={startup.image.alt}
              caption={startup.image.alt}
              clipStyle={ClippedImageStyle.innovationDetailHeader}
              plcSrc={{ src: startup.image.url, type: 'external' }}
            />
          </div>
        </div>
      </PlcPageDetailInnovationHeader>
      <InnovationDetailConcept
        className="background-ultralight"
        firstdescriptionquote={startup.firstBlockDescription}
        firstimagequote={startup.firstBlockImage}
        firstquote={startup.firstBlockQuote}
        quote={startup.generalQuote}
        seconddescriptionquote={startup.secondBlockDescription}
        secondimagequote={startup.secondBlockImage}
        secondquote={startup.secondBlockQuote}
        thirddescriptionquote={startup.thirdBlockDescription}
        thirdimagequote={startup.thirdBlockImage}
        thirdquote={startup.thirdBlockQuote}
      />
      <InnovationDetailBenefice
        beneficefirstdescription={startup.benefitFirstDescription}
        beneficeimage={startup.benefitImage}
        beneficeseconddescription={startup.benefitSecondDescription}
      />
      <InnovationDetailContact
        className="wrapper-s plc-py-l"
        description={startup.contactDescription}
        facebook={startup.contactFacebook}
        image={startup.contactImage}
        instagram={startup.contactInstagram}
        linkedin={startup.contactLinkedin}
        mail={startup.contactEmail}
        titlebutton={startup.titleButtonUrl}
        twitter={startup.contactTwitter}
        urlbutton={startupUrl}
      />
      <InnovationDetailSimilaire className="plc-pt-l background-ultralight" />
    </PlcPageLayout>
  );
};
